import axios from 'axios';
import { getConfig } from '../config/environment';
import {
  getAuthorizedContentAdminToken,
  getAdminRefreshToken,
  storeAdminTokens,
  clearAdminTokens,
} from '../utils/adminToken';
import { getMemberRequestToken } from './memberRequestToken.js';

const getBackendUrl = () => {
  const { backendUrl } = getConfig();
  return backendUrl?.replace(/\/$/, '') || '';
};

const backendUrl = getBackendUrl();

export const apiRoutes = {
  // Site
  SiteDetails: `${backendUrl}/frontend/site/details`,
  SiteWinners: `${backendUrl}/frontend/site/winners`,
  SiteDrawSponsor: `${backendUrl}/frontend/site/sponsor`,
  RecentSupporters: `${backendUrl}/frontend/site/recent-supporters`,
  PurchaseSummary: `${backendUrl}/frontend/site/purchase-summary`,

  // Draws
  AdditionalDraws: `${backendUrl}/frontend/site/organization/next/draw/additional`,

  // Organization
  OrganizationContactInformation: `${backendUrl}/frontend/site/organization/address`,
  OrganizationUpcomingDraws: `${backendUrl}/frontend/site/organization/draws`,
  OrganizationNextDraw: `${backendUrl}/frontend/site/organization/nextdraw`,
  OrganizationPurchasePlans: `${backendUrl}/frontend/site/organization/plans`,
  OrganizationContent: `${backendUrl}/frontend/site/organization/content`,

  // Player → organization inquiry ("Contact Us")
  MemberInquiry: `${backendUrl}/frontend/site/inquiry`,

  // UI translations (public, DB-backed). Append `/{languageCode}/{namespace}`.

  // Access
  LocationAccess: `${backendUrl}/frontend/site/organization/location/access`,

  // Email unsubscribe (from email link)
  EmailMemberUnsubscribe: `${backendUrl}/frontend/member/unsubscribe`,

  // Member
  MemberInformation: `${backendUrl}/frontend/member/information`,
  MemberTickets: `${backendUrl}/frontend/member/ticket`,
  OrderHistory: `${backendUrl}/frontend/member/orders/history`,
  UserWinHistory: `${backendUrl}/frontend/member/settings/winner/history`,
  UserHistory: `${backendUrl}/frontend/member/orders/summary`,
  MemberUnsubscribe: `${backendUrl}/frontend/member/settings/email/unsubscribe`,
  ChangePassword: `${backendUrl}/frontend/member/settings/password`,
  UpdateAnonymousWinner: `${backendUrl}/frontend/member/settings/anonymousWinner`,
  UpdateUserInformation: `${backendUrl}/frontend/member/settings/information`,
  EmailConfirmationResend: `${backendUrl}/frontend/member/authentication/resend`,
  MemberUpdate: `${backendUrl}/frontend/member`,

  // Auth - Email
  EmailLogin: `${backendUrl}/frontend/member/authentication/email/login`,
  EmailRegister: `${backendUrl}/frontend/member/authentication/email/register`,
  RefreshToken: `${backendUrl}/frontend/member/authentication/refresh`,
  ForgotPassword: `${backendUrl}/frontend/member/authentication/email/forgot`,
  PasswordReset: `${backendUrl}/frontend/member/authentication/email/password/reset`,
  ConfirmEmail: `${backendUrl}/frontend/member/email/confirm`,
  FinalizeAccount: `${backendUrl}/frontend/member/authentication/email/finalize`,
  SetInitialEmail: `${backendUrl}/frontend/member/authentication/email/setInitialEmail`,

  // Purchase
  MinimumPurchase: `${backendUrl}/frontend/site/purchase/minimum`,
  CheckPurchaseLimitations: `${backendUrl}/frontend/member/cart/limitations`,
  CtaPickACard: `${backendUrl}/frontend/site/pickacard`,
  Cart: `${backendUrl}/frontend/member/cart`,
  CartSummary: `${backendUrl}/frontend/member/cart/summary`,
  ClearCart: `${backendUrl}/frontend/member/cart/clear`,

  // Checkout
  InitializeCheckout: `${backendUrl}/frontend/member/checkout/initialize`,
  PaymentProviderInfo: `${backendUrl}/frontend/member/checkout/provider`,
  WorldPayProcessPayment: `${backendUrl}/frontend/member/checkout/worldpay/process`,
  MonerisProcessPayment: `${backendUrl}/frontend/member/checkout/moneris/process`,
  AdyenProcessPayment: `${backendUrl}/frontend/member/checkout/adyen/process`,
  AdyenProcessDetails: `${backendUrl}/frontend/member/checkout/adyen/details`,
  AdyenVerifyPayment: `${backendUrl}/frontend/member/checkout/adyen/verify`,
  PayPalConfirm: `${backendUrl}/frontend/member/checkout/paypal/confirm`,
  FiservProcessPayment: `${backendUrl}/frontend/member/checkout/fiserv/process`,
  NorthProcessPayment: `${backendUrl}/frontend/member/checkout/north/process`,
  NuveiProcessPayment: `${backendUrl}/frontend/member/checkout/nuvei/process`,
  ElavonProcessPayment: `${backendUrl}/frontend/member/checkout/elavon/process`,
  ElavonConvergeProcessPayment: `${backendUrl}/frontend/member/checkout/elavon-converge/process`,
  PaysafeProcessPayment: `${backendUrl}/frontend/member/checkout/paysafe/process`,
  StripePaymentIntent: `${backendUrl}/frontend/member/checkout/payment/stripe/intent`,
  CheckBillingAgainstGeo: `${backendUrl}/frontend/member/checkout/purchase/geo/billinginfo`,
  CheckoutDropdownFields: `${backendUrl}/frontend/site/checkout/userdefined/fields`,
  CheckoutDropdownFieldsTitle: `${backendUrl}/frontend/site/checkout/userdefined/Title`,
  GuestCheckoutCreate: `${backendUrl}/frontend/guest/checkout/create`,
  GuestAnonymousCartAdd: `${backendUrl}/frontend/guest/cart`,
  GuestAnonymousInitialize: `${backendUrl}/frontend/guest/checkout/initialize`,
  GuestAnonymousComplete: `${backendUrl}/frontend/guest/checkout/complete`,
  GuestAnonymousMonerisProcess: `${backendUrl}/frontend/guest/checkout/moneris/process`,
  GuestAnonymousNuveiProcess: `${backendUrl}/frontend/guest/checkout/nuvei/process`,
  GuestAnonymousElavonProcess: `${backendUrl}/frontend/guest/checkout/elavon/process`,
  GuestAnonymousElavonConvergeProcess: `${backendUrl}/frontend/guest/checkout/elavon-converge/process`,
  GuestAnonymousPaysafeProcess: `${backendUrl}/frontend/guest/checkout/paysafe/process`,
  GuestAnonymousReceipt: `${backendUrl}/frontend/guest/orders/receipt`,

  // Receipts
  CurrentPurchaseReceipt: `${backendUrl}/frontend/member/orders/current/receipt`,
  PurchaseReceipt: `${backendUrl}/frontend/member/orders/receipt`,
  ResendPurchaseReceipt: `${backendUrl}/frontend/member/orders/purchase/receipt/resend`,

  // Ticket Tracking
  TicketTrackingFields: `${backendUrl}/frontend/site/ticket/tracking/field`,
  TicketTrackingOptions: `${backendUrl}/frontend/site/ticket/tracking/entry`,
  TicketTrackingUpdate: `${backendUrl}/frontend/member/ticket/tracking`,
  TicketTrackingPartners: `${backendUrl}/frontend/site/organization/partners`,

  // Member — Square Game
  MemberMySquares:     `${backendUrl}/frontend/member/my-squares`,
  MemberRefundRequest: `${backendUrl}/frontend/member/refund-request`,
  MemberRefundCancel:  `${backendUrl}/frontend/member/refund-cancel`,
  ReleaseSquareHold:   `${backendUrl}/frontend/member/cart/square`,

  // Game-specific API routes
  SquareGameGrid: `${backendUrl}/frontend/games/square-game/squares`,
  SuperballBalls: `${backendUrl}/frontend/games/superball/balls`,
  EliminationMilestones: `${backendUrl}/frontend/games/elimination/milestones`,
  PrizeDrawPrizes: `${backendUrl}/frontend/games/prize-draw/prizes`,

  // Content admin auth
  AdminLogin:        `${backendUrl}/admin/auth/login`,
  AdminVerifyMFA:    `${backendUrl}/admin/auth/mfa/login`,
  AdminSendMFASms:   `${backendUrl}/admin/auth/mfa/sms/send`,
  AdminVerifyMFASms: `${backendUrl}/admin/auth/mfa/sms/login`,
  AdminMFAStatus:    `${backendUrl}/admin/auth/mfa/status`,
  AdminSwitchOrgs:   `${backendUrl}/admin/auth/switchorgs`,
  AdminLogout:       `${backendUrl}/admin/auth/logout`,
  // WebAuthn (passkey) – admin auth
  AdminWebAuthnLoginOptions:    `${backendUrl}/admin/auth/webauthn/login/options`,
  AdminWebAuthnLoginComplete:   `${backendUrl}/admin/auth/webauthn/login/complete`,
  AdminWebAuthnRegisterOptions: `${backendUrl}/admin/auth/webauthn/register/options`,
  AdminWebAuthnRegisterComplete:`${backendUrl}/admin/auth/webauthn/register/complete`,
  AdminWebAuthnCredentials:     `${backendUrl}/admin/auth/webauthn/credentials`,
  AdminUpdateContent:`${backendUrl}/admin/organization/content`,

  // Page sections (admin-only — requires content_admin_token)
  PageSections:      `${backendUrl}/admin/organization/page-sections`,
  // Public page sections (anonymous access)
  PublicPageSections: `${backendUrl}/frontend/site/organization/page-sections`,

  // Custom Pages (admin-only — requires content_admin_token)
  AdminCustomPages:  `${backendUrl}/admin/organization/custom-pages`,
  // Public custom pages (anonymous access)
  PublicCustomPages: `${backendUrl}/frontend/site/organization/custom-pages`,

  // Organization appearance (admin-only — PATCH to update style fields)
  AdminUpdateOrganization: `${backendUrl}/admin/organization`,

  // Organization fundraising goal (admin-only — GET/PUT org-level or per-draw goal)
  AdminOrganizationGoal: `${backendUrl}/admin/organization/goal`,

  // Grid Layout Engine (Phase 2)
  // Public endpoint - returns published layout for current org/gameType
  GridLayoutPublic: `${backendUrl}/frontend/site/organization/grid-layout`,
  // Admin endpoints - manage custom layouts (requires content_admin_token)
  AdminGridLayout: `${backendUrl}/admin/organization/grid-layout`,

  // Site Draft (Draft/Live mode)
  SiteDraft:        `${backendUrl}/admin/organization/site-draft`,
  SiteDraftPublish: `${backendUrl}/admin/organization/site-draft/publish`,

  // AI content generation (PlatformAdmin only) — used by translation/content editors
  AdminContentGenerateHtml: `${backendUrl}/admin/content/generate-html`,

  // Chase the Ace admin draw actions
  CtaTriggerDraw: `${backendUrl}/admin/organization/cta/trigger-draw`,

  // Admin token refresh
  AdminRefreshToken: `${backendUrl}/admin/auth/refresh`,
};

const instance = axios.create({
  baseURL: backendUrl
});

/** True when the request URL targets an admin endpoint */
function isAdminRequest(url) {
  if (!url) return false;
  try {
    const path = new URL(url, 'http://localhost').pathname;
    return path.includes('/admin/');
  } catch {
    return String(url).includes('/admin/');
  }
}

instance.interceptors.request.use(
  config => {
    // If a token is already set explicitly (e.g. content admin save), leave it
    if (!config.headers.Authorization) {
      if (isAdminRequest(config.url)) {
        // Admin endpoints: prefer valid content-admin token, never fall back to member token
        const adminToken = getAuthorizedContentAdminToken(getConfig().tlfId);
        if (adminToken) {
          config.headers.Authorization = `Bearer ${adminToken}`;
        }
        // If no valid admin token, send without auth — the 401 interceptor will
        // attempt a refresh or the caller will handle the rejection.
      } else {
        // Non-admin endpoints: member token first, then admin token as fallback
        const token = getMemberRequestToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  },
  error => Promise.reject(error)
);

// Admin-only 401 refresh queue — serializes concurrent admin refresh attempts
let adminRefreshPromise = null;

// Member 401 refresh queue — serializes concurrent member refresh attempts
let memberRefreshPromise = null;

async function refreshMemberToken() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('No member refresh token available');
  }
  try {
    const { tlfId } = getConfig();
    const res = await axios.post(
      `${getBackendUrl()}${apiRoutes.RefreshToken}?organizationId=${tlfId}`,
      {},
      { headers: { Authorization: `Bearer ${refreshToken}` } }
    );
    const data = res.data?.payload || res.data;
    const newAccess = data?.accessToken || data?.AccessToken;
    const newRefresh = data?.refreshToken || data?.RefreshToken;
    if (!newAccess) throw new Error('No access token in refresh response');
    localStorage.setItem('access_token', newAccess);
    if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
    if (data?.expiresAt || data?.ExpiresAt) {
      localStorage.setItem('expires_at', data.expiresAt || data.ExpiresAt);
    }
    return newAccess;
  } catch {
    // Clear auth and notify listeners
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_at');
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
    throw new Error('Member token refresh failed');
  }
}

async function refreshAdminToken() {
  const refreshToken = getAdminRefreshToken();
  if (!refreshToken) {
    clearAdminTokens();
    throw new Error('No admin refresh token available');
  }
  try {
    const res = await axios.post(apiRoutes.AdminRefreshToken, { refreshToken });
    const newAccess = res.data?.accessToken || res.data?.AccessToken;
    const newRefresh = res.data?.refreshToken || res.data?.RefreshToken;
    if (!newAccess) throw new Error('No access token in refresh response');
    storeAdminTokens(newAccess, newRefresh || refreshToken);
    return newAccess;
  } catch {
    clearAdminTokens();
    throw new Error('Admin token refresh failed');
  }
}

instance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Admin 401 handling
    if (isAdminRequest(originalRequest?.url)) {
      originalRequest._retry = true;

      // Serialize concurrent refresh attempts
      if (!adminRefreshPromise) {
        adminRefreshPromise = refreshAdminToken().finally(() => {
          adminRefreshPromise = null;
        });
      }

      try {
        const newToken = await adminRefreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch {
        // Refresh failed — propagate the original 401
        return Promise.reject(error);
      }
    }

    // Member 401 handling — attempt token refresh and retry
    const hadToken = !!getMemberRequestToken() || !!localStorage.getItem('refresh_token');
    if (hadToken) {
      originalRequest._retry = true;

      if (!memberRefreshPromise) {
        memberRefreshPromise = refreshMemberToken().finally(() => {
          memberRefreshPromise = null;
        });
      }

      try {
        const newToken = await memberRefreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * releaseSquareHold — Helper to release a square hold with 404 swallowing.
 * 
 * A 404 response means "no active hold found," which is the desired state.
 * Treat it as success (idempotent) rather than surfacing an error.
 * 
 * @param {number} squareNumber - Square number to release
 * @returns {Promise<void>}
 */
export async function releaseSquareHold(squareNumber) {
  try {
    await instance.delete(`${apiRoutes.ReleaseSquareHold}?SquareNumber=${squareNumber}`);
  } catch (err) {
    // 404 or errorKeys containing 'error.member.squareHoldNotFound' means
    // the hold doesn't exist — that's the desired state, so treat as success
    const is404 = err?.response?.status === 404;
    const hasNotFoundKey = err?.response?.data?.errorKeys?.includes('error.member.squareHoldNotFound');
    
    if (!is404 && !hasNotFoundKey) {
      // Real error — rethrow
      throw err;
    }
    // Otherwise swallow: no hold found = success
  }
}

export default instance;
